import { MESSAGING_LIMITS } from '../enums';
import {
  type BlockNode,
  CODE_LANGUAGE_PATTERN,
  type ChannelLinkNode,
  type InlineNode,
  LINK_HREF_PATTERN,
  type ListItemNode,
  MAX_LINK_HREF_LENGTH,
  type MentionNode,
  type RichTextDoc,
  type RichTextMark,
  type TextNode,
} from './schema';

/**
 * Markdown shortcuts → the D-045 rich-text document, for composers without a rich editor (the
 * mobile composer; bots may use it later). People type what chat apps have taught them:
 * `*bold*` or `**bold**`, `_italic_`, `~strike~` or `~~strike~~`, `` `code` ``, fenced code blocks
 * (and a one-line ```` ```code``` ````), `> quote`, `- `, `* `, `+ ` and `1. ` lists (nesting by
 * indentation), `[text](https://…)` and bare links, `:shortcode:` emoji, and the mention and
 * channel tokens the composer inserted (`options.mentions`).
 *
 * This is deliberately NOT the dialect of `tokens.ts` (the edge codec for bots and exports, where
 * `*x*` is italic and ids travel as `<@u:…>`): here a single `*` means bold, as in the chat apps
 * people type it from (D-542). `toTokenText()` output still parses back, because `**`, `_`, `~~`,
 * code, links, quotes, lists and fences mean the same in both.
 *
 * Emphasis follows boundary rules so ordinary prose survives a phone keyboard: an opening
 * delimiter needs a non-space next to it and a non-letter (any script, marks included) before it,
 * a closing one the mirror image. `snake_case_name`, `2*3*4`, `a * b * c` and Devanagari words are
 * never split. A backslash escapes `\* \_ \~ \` \[ …`. Emphasis does not cross a line break.
 *
 * Text is NFC-normalised as typed and control characters are dropped, so the result passes
 * `validateRichText()`; plain text in any script (Devanagari included) round-trips unchanged
 * through `toPlainText`. Parsing is linear in the input (PLAN §6 Phase 1 risk "oversized
 * documents"): the line is scanned once left to right, code spans and emphasis closers use
 * forward-only pointers, link labels stop at the next bracket, and the emphasis stack is bounded.
 */

export interface FromMarkdownOptions {
  /**
   * The mentions the composer inserted, keyed by the token it put into the text (`@Pooja Gupta`,
   * `@here`, `#sales`). Text that is not a known token stays text. A token matches where it is not
   * glued to a letter on either side (`@Pooja Guptaji` is not `@Pooja Gupta`).
   */
  mentions?: ReadonlyMap<string, MentionNode | ChannelLinkNode>;
  /** Emoji shortcodes that exist (`@emoji-mart/data` ids plus custom emoji names). */
  isEmoji?: (shortcode: string) => boolean;
}

// ================================================================ characters

const WORD = /[\p{L}\p{N}\p{M}]/u;
const SPACE = /\s/u;
const LINE_SEPARATORS = /[\u2028\u2029]/g;
// eslint-disable-next-line no-control-regex -- dropping control characters is the point
const CONTROL_EXCEPT_TAB_NEWLINE = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g;

function charBefore(text: string, index: number): string {
  if (index <= 0) return '';
  const low = text.charCodeAt(index - 1);
  if (low >= 0xdc00 && low <= 0xdfff && index >= 2) {
    const high = text.charCodeAt(index - 2);
    if (high >= 0xd800 && high <= 0xdbff) return text.slice(index - 2, index);
  }
  return text.charAt(index - 1);
}

function charAt(text: string, index: number): string {
  if (index >= text.length) return '';
  return String.fromCodePoint(text.codePointAt(index) ?? 0);
}

const isWord = (char: string): boolean => char !== '' && WORD.test(char);
const isSpace = (char: string): boolean => char !== '' && SPACE.test(char);

// ================================================================ inline

const MAX_EMPHASIS_DEPTH = 16;
const ESCAPABLE = /[\\`*_~[\]()<>#@!.+\-:|]/;
/** Characters an inline rule may start at; everything between two of them is plain text. */
const TRIGGERS = /[\\`[*_~@#:hHwW]/g;
const URL_START = /(?:https?:\/\/|www\.)/iy;
const WWW_HOST = /www\.[\p{L}\p{N}][\p{L}\p{N}-]*\.\p{L}{2,}/iuy;
const URL_BODY = /^[^\s<>"'`]+/;
const TRAILING_URL_PUNCTUATION = '.,;:!?]}\'"*_~';
const EMOJI_TOKEN = /:([A-Za-z0-9_+-]{1,64}(?:::skin-tone-[2-6])?):/y;
const LINK_TARGET = /(?:https?:\/\/|mailto:)[^\s)<>"'`[\]]+/iy;

type DelimiterChar = '*' | '_' | '~';

interface Delimiter {
  kind: 'delim';
  char: DelimiterChar;
  length: number;
  canOpen: boolean;
  canClose: boolean;
  /** Set when matched: the marks the pair applies to what lies between. */
  marks: RichTextMark[] | null;
  role: 'open' | 'close' | null;
}

type InlineItem =
  | { kind: 'text'; text: string; marks: readonly RichTextMark[] }
  | { kind: 'nodes'; nodes: InlineNode[] }
  | Delimiter;

interface MentionIndex {
  /** Tokens by their first character, longest first. */
  byFirst: Map<string, { token: string; node: MentionNode | ChannelLinkNode }[]>;
}

interface Context {
  mentions: MentionIndex | null;
  isEmoji: ((shortcode: string) => boolean) | null;
}

interface RuleMatch {
  end: number;
  items: InlineItem[];
}

function buildMentionIndex(
  mentions: ReadonlyMap<string, MentionNode | ChannelLinkNode> | undefined,
): MentionIndex | null {
  if (!mentions || mentions.size === 0) return null;
  const byFirst: MentionIndex['byFirst'] = new Map();
  for (const [rawToken, node] of mentions) {
    const token = rawToken.normalize('NFC');
    const first = token.charAt(0);
    if ((first !== '@' && first !== '#') || token.length < 2) continue;
    const list = byFirst.get(first) ?? [];
    list.push({ token, node });
    byFirst.set(first, list);
  }
  for (const list of byFirst.values()) list.sort((a, b) => b.token.length - a.token.length);
  return byFirst.size > 0 ? { byFirst } : null;
}

function withMarks(
  base: readonly RichTextMark[],
  extra: readonly RichTextMark[],
): readonly RichTextMark[] {
  if (extra.length === 0) return base;
  const out = [...base];
  for (const mark of extra) {
    if (!out.some((m) => m.type === mark.type)) out.push(mark);
  }
  return out;
}

/** Marks are written in one order, whatever order the delimiters nested in. */
const MARK_ORDER: Record<RichTextMark['type'], number> = {
  bold: 0,
  italic: 1,
  strike: 2,
  code: 3,
  link: 4,
};

function textNode(text: string, marks: readonly RichTextMark[]): TextNode {
  if (marks.length === 0) return { type: 'text', text };
  return {
    type: 'text',
    text,
    marks: [...marks].sort((a, b) => MARK_ORDER[a.type] - MARK_ORDER[b.type]),
  };
}

function sameMarks(a: readonly RichTextMark[] = [], b: readonly RichTextMark[] = []): boolean {
  if (a.length !== b.length) return false;
  const key = (m: RichTextMark) => (m.type === 'link' ? `link:${m.attrs.href}` : m.type);
  const keys = new Set(b.map(key));
  return a.every((m) => keys.has(key(m)));
}

/** Merges adjacent text nodes with the same marks and drops empty ones. */
function mergeText(nodes: readonly InlineNode[]): InlineNode[] {
  const out: InlineNode[] = [];
  let marks: readonly RichTextMark[] | undefined;
  let parts: string[] = [];
  for (const node of nodes) {
    if (node.type === 'text') {
      if (node.text === '') continue;
      if (parts.length > 0 && sameMarks(marks, node.marks)) {
        parts.push(node.text);
        continue;
      }
      if (parts.length > 0) out.push(textNode(parts.join(''), marks ?? []));
      marks = node.marks;
      parts = [node.text];
    } else {
      if (parts.length > 0) out.push(textNode(parts.join(''), marks ?? []));
      parts = [];
      out.push(node);
    }
  }
  if (parts.length > 0) out.push(textNode(parts.join(''), marks ?? []));
  return out;
}

function emphasisMarks(char: DelimiterChar, length: number): RichTextMark[] | null {
  if (char === '~') return length <= 2 ? [{ type: 'strike' }] : null;
  if (length === 1) return [{ type: char === '*' ? 'bold' : 'italic' }];
  if (length === 2) return [{ type: 'bold' }];
  if (length === 3) return [{ type: 'bold' }, { type: 'italic' }];
  return null;
}

/** Code spans: a run of n backticks closes at the next run of exactly n (CommonMark). */
class BacktickRuns {
  private readonly byLength = new Map<number, { starts: number[]; next: number }>();

  constructor(line: string) {
    for (const match of line.matchAll(/`+/g)) {
      const entry = this.byLength.get(match[0].length) ?? { starts: [], next: 0 };
      entry.starts.push(match.index);
      this.byLength.set(match[0].length, entry);
    }
  }

  /** The start of the first run of `length` backticks at or after `from`, or -1. */
  closerFrom(length: number, from: number): number {
    const entry = this.byLength.get(length);
    if (!entry) return -1;
    while (entry.next < entry.starts.length && entry.starts[entry.next]! < from) entry.next++;
    return entry.starts[entry.next] ?? -1;
  }
}

/**
 * Trailing punctuation belongs to the sentence, not the address: `see https://x.com/a.` and
 * `(https://x.com/a)` link `https://x.com/a`, while `https://en.wikipedia.org/wiki/Foo_(bar)` keeps
 * its closing parenthesis (it balances one inside the address). Linear in the length.
 */
function stripTrailingUrlPunctuation(url: string): string {
  let open = 0;
  let close = 0;
  for (const char of url) {
    if (char === '(') open++;
    else if (char === ')') close++;
  }
  let end = url.length;
  while (end > 0) {
    const char = url.charAt(end - 1);
    if (char === ')') {
      if (close <= open) break;
      close--;
    } else if (!TRAILING_URL_PUNCTUATION.includes(char)) {
      break;
    }
    end--;
  }
  return url.slice(0, end);
}

function validHref(href: string): boolean {
  return href.length <= MAX_LINK_HREF_LENGTH && LINK_HREF_PATTERN.test(href);
}

function tryCode(line: string, pos: number, runs: BacktickRuns): RuleMatch | null {
  let length = 1;
  while (line[pos + length] === '`') length++;
  const close = runs.closerFrom(length, pos + length);
  if (close === -1)
    return { end: pos + length, items: [{ kind: 'text', text: '`'.repeat(length), marks: [] }] };
  let content = line.slice(pos + length, close);
  if (content.length >= 2 && content.startsWith(' ') && content.endsWith(' ') && content.trim()) {
    content = content.slice(1, -1);
  }
  if (content === '') {
    return { end: pos + length, items: [{ kind: 'text', text: '`'.repeat(length), marks: [] }] };
  }
  return {
    end: close + length,
    items: [{ kind: 'text', text: content, marks: [{ type: 'code' }] }],
  };
}

function tryLink(line: string, pos: number, ctx: Context): RuleMatch | null {
  let j = pos + 1;
  while (j < line.length && line[j] !== ']' && line[j] !== '[') j++;
  if (line[j] !== ']' || j === pos + 1 || line[j + 1] !== '(') return null;
  LINK_TARGET.lastIndex = j + 2;
  const target = LINK_TARGET.exec(line);
  if (!target || line[j + 2 + target[0].length] !== ')') return null;
  const href = target[0];
  if (!validHref(href)) return null;
  const label = line.slice(pos + 1, j);
  if (label.trim() === '') return null;
  const nodes = parseInline(label, ctx, [{ type: 'link', attrs: { href } }], true);
  if (nodes.length === 0) return null;
  return { end: j + 2 + href.length + 1, items: [{ kind: 'nodes', nodes }] };
}

function tryAutolink(line: string, pos: number): RuleMatch | null {
  if (isWord(charBefore(line, pos))) return null;
  URL_START.lastIndex = pos;
  const start = URL_START.exec(line);
  if (!start) return null;
  const www = start[0].toLowerCase() === 'www.';
  if (www) {
    WWW_HOST.lastIndex = pos;
    if (!WWW_HOST.test(line)) return null;
  }
  // Scan at most one character past the longest address we would link: a longer one is text, and
  // an unbounded scan from every `http` of a long run of them would be quadratic.
  const body = URL_BODY.exec(line.slice(pos, pos + MAX_LINK_HREF_LENGTH + 2));
  if (!body) return null;
  const url = stripTrailingUrlPunctuation(body[0]);
  const href = www ? `https://${url}` : url;
  if (url.length <= start[0].length || !validHref(href)) return null;
  return {
    end: pos + url.length,
    items: [{ kind: 'nodes', nodes: [textNode(url, [{ type: 'link', attrs: { href } }])] }],
  };
}

function tryMention(line: string, pos: number, ctx: Context): RuleMatch | null {
  const candidates = ctx.mentions?.byFirst.get(line.charAt(pos));
  if (!candidates || isWord(charBefore(line, pos))) return null;
  for (const { token, node } of candidates) {
    if (!line.startsWith(token, pos)) continue;
    if (isWord(charAt(line, pos + token.length))) continue;
    const copy: MentionNode | ChannelLinkNode =
      node.type === 'mention'
        ? { type: 'mention', attrs: { ...node.attrs } }
        : { type: 'channelLink', attrs: { ...node.attrs } };
    return { end: pos + token.length, items: [{ kind: 'nodes', nodes: [copy] }] };
  }
  return null;
}

function tryEmoji(line: string, pos: number, ctx: Context): RuleMatch | null {
  if (!ctx.isEmoji || isWord(charBefore(line, pos))) return null;
  EMOJI_TOKEN.lastIndex = pos;
  const match = EMOJI_TOKEN.exec(line);
  if (!match) return null;
  const name = match[1]!.toLowerCase();
  if (!ctx.isEmoji(name)) return null;
  return {
    end: pos + match[0].length,
    items: [{ kind: 'nodes', nodes: [{ type: 'emoji', attrs: { name } }] }],
  };
}

function delimiterRun(line: string, pos: number): Delimiter & { end: number } {
  const char = line.charAt(pos) as DelimiterChar;
  let end = pos + 1;
  while (line[end] === char) end++;
  const before = charBefore(line, pos);
  const after = charAt(line, end);
  const length = end - pos;
  const usable = emphasisMarks(char, length) !== null;
  return {
    kind: 'delim',
    char,
    length,
    end,
    canOpen: usable && after !== '' && !isSpace(after) && !isWord(before),
    canClose: usable && before !== '' && !isSpace(before) && !isWord(after),
    marks: null,
    role: null,
  };
}

/** Pairs each closing delimiter with the nearest opener of the same kind; the rest stay text. */
function resolveDelimiters(items: InlineItem[]): void {
  const stack: Delimiter[] = [];
  for (const item of items) {
    if (item.kind !== 'delim') continue;
    if (item.canClose) {
      let found = -1;
      for (let i = stack.length - 1; i >= 0; i--) {
        const opener = stack[i]!;
        if (opener.char === item.char && opener.length === item.length) {
          found = i;
          break;
        }
      }
      if (found !== -1) {
        const opener = stack[found]!;
        const marks = emphasisMarks(item.char, item.length)!;
        opener.marks = marks;
        opener.role = 'open';
        item.marks = marks;
        item.role = 'close';
        stack.length = found;
        continue;
      }
    }
    if (item.canOpen && stack.length < MAX_EMPHASIS_DEPTH) stack.push(item);
  }
}

function emit(items: readonly InlineItem[], base: readonly RichTextMark[]): InlineNode[] {
  const out: InlineNode[] = [];
  const active: RichTextMark[][] = [];
  const current = (): readonly RichTextMark[] => active.reduce(withMarks, base);
  for (const item of items) {
    if (item.kind === 'text') {
      out.push(textNode(item.text, withMarks(current(), item.marks)));
    } else if (item.kind === 'nodes') {
      const marks = current();
      for (const node of item.nodes) {
        out.push(
          node.type === 'text' ? textNode(node.text, withMarks(marks, node.marks ?? [])) : node,
        );
      }
    } else if (item.role === 'open' && item.marks) {
      active.push(item.marks);
    } else if (item.role === 'close') {
      active.pop();
    } else {
      out.push(textNode(item.char.repeat(item.length), current()));
    }
  }
  return mergeText(out);
}

function parseInline(
  line: string,
  ctx: Context,
  base: readonly RichTextMark[],
  inLink: boolean,
): InlineNode[] {
  const items: InlineItem[] = [];
  let literal: string[] = [];
  const flush = () => {
    if (literal.length > 0) items.push({ kind: 'text', text: literal.join(''), marks: [] });
    literal = [];
  };
  const push = (match: RuleMatch): number => {
    flush();
    items.push(...match.items);
    return match.end;
  };
  let runs: BacktickRuns | null = null;
  let pos = 0;
  while (pos < line.length) {
    TRIGGERS.lastIndex = pos;
    const trigger = TRIGGERS.exec(line);
    const next = trigger ? trigger.index : line.length;
    if (next > pos) {
      literal.push(line.slice(pos, next));
      pos = next;
      if (pos >= line.length) break;
    }
    const char = line.charAt(pos);
    let match: RuleMatch | null = null;
    switch (char) {
      case '\\': {
        const escaped = line.charAt(pos + 1);
        if (escaped && ESCAPABLE.test(escaped)) {
          literal.push(escaped);
          pos += 2;
        } else {
          literal.push('\\');
          pos += 1;
        }
        continue;
      }
      case '`':
        runs ??= new BacktickRuns(line);
        match = tryCode(line, pos, runs);
        break;
      case '[':
        if (!inLink) match = tryLink(line, pos, ctx);
        break;
      case 'h':
      case 'H':
      case 'w':
      case 'W':
        if (!inLink) match = tryAutolink(line, pos);
        break;
      case '@':
      case '#':
        match = tryMention(line, pos, ctx);
        break;
      case ':':
        match = tryEmoji(line, pos, ctx);
        break;
      default: {
        const run = delimiterRun(line, pos);
        flush();
        items.push(run);
        pos = run.end;
        continue;
      }
    }
    if (match) {
      pos = push(match);
    } else {
      literal.push(char);
      pos += 1;
    }
  }
  flush();
  resolveDelimiters(items);
  return emit(items, base);
}

// ================================================================ blocks

const FENCE_OPEN = /^```[ \t]*([A-Za-z0-9+#._-]*)[ \t]*$/;
const FENCE_CLOSE = /^```[ \t]*$/;
const FENCE_ONE_LINE = /^```([^`]+)```[ \t]*$/;
const QUOTE = /^>/;
/** Ordered markers stop at six digits: the validator caps `orderedList.start` at 1,000,000. */
const LIST_ITEM = /^( *)([-*+•]|\d{1,6}[.)]) +(\S.*)$/;
const MAX_BLOCK_DEPTH = MESSAGING_LIMITS.maxContentDepth - 1;

function paragraph(lines: readonly string[], ctx: Context): BlockNode {
  const content: InlineNode[] = [];
  lines.forEach((line, index) => {
    if (index > 0) content.push({ type: 'hardBreak' });
    content.push(...parseInline(line, ctx, [], false));
  });
  const merged = mergeText(content);
  return merged.length > 0 ? { type: 'paragraph', content: merged } : { type: 'paragraph' };
}

function codeBlock(language: string, text: string): BlockNode {
  const lang = language.toLowerCase();
  const attrs = { language: CODE_LANGUAGE_PATTERN.test(lang) ? lang : null };
  return text
    ? { type: 'codeBlock', attrs, content: [{ type: 'text', text }] }
    : { type: 'codeBlock', attrs };
}

/**
 * A list may interrupt a paragraph only when it is a bullet list or an ordered list starting at 1
 * (CommonMark), so "…\n2024. was a good year" stays a paragraph.
 */
function interruptsParagraph(line: string, depth: number): boolean {
  if (FENCE_OPEN.test(line)) return true;
  if (depth >= MAX_BLOCK_DEPTH) return false;
  if (QUOTE.test(line)) return true;
  const item = LIST_ITEM.exec(line);
  if (!item) return false;
  return !/\d/.test(item[2]!) || Number.parseInt(item[2]!, 10) === 1;
}

function parseList(
  lines: readonly string[],
  start: number,
  depth: number,
  ctx: Context,
): { block: BlockNode; next: number } {
  const first = LIST_ITEM.exec(lines[start]!)!;
  const indent = first[1]!.length;
  const ordered = /\d/.test(first[2]!);
  const items: ListItemNode[] = [];
  let i = start;
  while (i < lines.length) {
    const match = LIST_ITEM.exec(lines[i]!);
    if (!match || match[1]!.length !== indent || /\d/.test(match[2]!) !== ordered) break;
    const itemLines = [match[3]!];
    // Continuation lines and nested lists are indented further than this item's marker.
    const contentIndent = indent + match[2]!.length + 1;
    i++;
    while (i < lines.length) {
      const line = lines[i]!;
      if (line.trim() === '') break;
      const lead = /^ */.exec(line)![0].length;
      if (lead <= indent) break;
      itemLines.push(line.slice(Math.min(lead, contentIndent)));
      i++;
    }
    const content = parseBlocks(itemLines, depth + 1, ctx);
    items.push({
      type: 'listItem',
      content: content[0]?.type === 'paragraph' ? content : [{ type: 'paragraph' }, ...content],
    });
  }
  if (ordered) {
    return {
      block: {
        type: 'orderedList',
        attrs: { start: Number.parseInt(first[2]!, 10) },
        content: items,
      },
      next: i,
    };
  }
  return { block: { type: 'bulletList', content: items }, next: i };
}

function parseBlocks(lines: readonly string[], depth: number, ctx: Context): BlockNode[] {
  const blocks: BlockNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') {
      i++;
      continue;
    }
    const fence = FENCE_OPEN.exec(line);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !FENCE_CLOSE.test(lines[i]!)) {
        body.push(lines[i]!);
        i++;
      }
      i++; // the closing fence (a missing one runs to the end, as in CommonMark)
      blocks.push(codeBlock(fence[1]!, body.join('\n')));
      continue;
    }
    const oneLine = FENCE_ONE_LINE.exec(line);
    if (oneLine) {
      blocks.push(codeBlock('', oneLine[1]!.trim()));
      i++;
      continue;
    }
    if (depth < MAX_BLOCK_DEPTH && QUOTE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) {
        inner.push(lines[i]!.replace(/^> ?/, ''));
        i++;
      }
      const content = parseBlocks(inner, depth + 1, ctx);
      blocks.push({
        type: 'blockquote',
        content: content.length > 0 ? content : [{ type: 'paragraph' }],
      });
      continue;
    }
    if (depth < MAX_BLOCK_DEPTH && LIST_ITEM.test(line)) {
      const { block, next } = parseList(lines, i, depth, ctx);
      blocks.push(block);
      i = next;
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== '' &&
      (para.length === 0 || !interruptsParagraph(lines[i]!, depth))
    ) {
      para.push(lines[i]!);
      i++;
    }
    blocks.push(paragraph(para, ctx));
  }
  return blocks;
}

/**
 * Typed text with markdown shortcuts → a message document. Empty or blank input gives one empty
 * paragraph (`isEmptyDoc()` is true for it).
 */
export function fromMarkdown(text: string, options: FromMarkdownOptions = {}): RichTextDoc {
  const ctx: Context = {
    mentions: buildMentionIndex(options.mentions),
    isEmoji: options.isEmoji ?? null,
  };
  const lines = text
    .normalize('NFC')
    .replace(/\r\n?/g, '\n')
    .replace(LINE_SEPARATORS, '\n')
    .replace(CONTROL_EXCEPT_TAB_NEWLINE, '')
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/, ''));
  const content = parseBlocks(lines, 0, ctx);
  return { type: 'doc', content: content.length > 0 ? content : [{ type: 'paragraph' }] };
}
